import snakeCaseKeys from 'snakecase-keys';

import { restClient } from '../../client';
import { User, UserContact, UserPrefix, UserSuffix } from '../../resources';
import { ContactsResponse } from '../contacts';
import { HigherEdOrg } from './org';

export enum CareType {
  therapy = 'therapy',
  psychiatry = 'psychiatry',
  therapyAndPsychiatry = 'therapy_and_psychiatry'
}

export type StudentReferralInput = {
  student: {
    email: string;
    phoneNumber: string;
    orgId: string;
    firstName: string;
    lastName: string;
  };
  referralNote: {
    careType: CareType | CareType[];
    content?: string;
  };
};

export type StudentByTokenInput = {
  token: string;
};

export const enum StudentInviteStatus {
  invited = 'invited',
  accepted = 'accepted',
  revoked = 'revoked',
}

export const enum StudentStatus {
  active = 'active',
  inactive = 'inactive',
}

export type GetStudentByInviteTokenResponse = {
  data: {
    firstName: string;
    lastName: string;
    email: string;
    careType: CareType;
    contacts: UserContact[];
    orgName: string;
  };
};

export type Student = {
  user: User;
  inviteStatus: StudentInviteStatus;
  status: StudentStatus;
  orgId: string;
};

export type StudentInviteToken = {
  orgId: string;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  inviterId: string;
  inviteeId: string;
  type: 'student';
};

export enum CareStatus {
  initial = 'initial',
  established = 'established',
  archived = 'archived'
}

export type ActiveCareRelations = {
  provider: {
    id: string;
    displayName: string;
    email: string;
    prefix?: UserPrefix; //TODO: ask backend to add prefix and suffixes to provider
    suffixes?: UserSuffix[];
  };
  status: string;
};

export type CareHistory = {
  patient: User & {
    tags: string[];
  };
  activeCareRelations: {
    therapy: ActiveCareRelations[];
    psychiatry: ActiveCareRelations;
    status: CareStatus;
  };
  patientAttendedAppointment: boolean;
};

export type StudentReferral = {
  fromUserId: string;
  aboutUserId: string;
  content: string;
  careType: CareType;
  forUserId: string ;
  createdAt: string;
  updatedAt: string;
};

export type HigherEdStudentDetails = {
  userId: string;
  inviteStatus: StudentInviteStatus;
  status: StudentStatus;
  org: HigherEdOrg;
};

export const sendStudentReferral = async (data: StudentReferralInput) => {
  return restClient.request<{ data: { userId: string } }>('/api/v1/higher_ed/student/invite',
    { method: 'POST', data: snakeCaseKeys(data) });
};

export const resendStudentInvite = async (studentId: string) => {
  return restClient.request<{ data: Record<string, never>  }>(`/api/v1/higher_ed/student/${studentId}/invite/resend`,
    { method: 'POST', data: {} });
};

export const getStudentByInviteToken = async (token: string) => {
  return restClient.request<GetStudentByInviteTokenResponse>(`/api/v1/view/higher_ed/student/invite/by_token/${token}`,
    { method: 'GET' });
};

export const getStudentsByOrgId = async (orgId: string) => {
  return restClient.request<{ data: Student[] }>(`api/v1/higher_ed/student/by_org_id/${orgId}`,
    { method: 'GET' });
};

export const getStudentByStudentId = async (studentId: string) => {
  return restClient.request<{ data: Student }>(`api/v1/higher_ed/student/${studentId}`,
    { method: 'GET' });
};

export const getCareHistoryByPatientIds = async (ids: string[]) => {
  return restClient.request<{ data: { students: Record<string, CareHistory>} }>('api/v1/view/care_history/by_patient_ids',
    { method: 'POST', data: { ids } });
};

export const getAppointmentHistoryByPatientIds = async (ids: string[]) => {
  return restClient.request<{ data: Record<string, number> }>('api/v1/view/appointment_history/by_patient_ids',
    { method: 'POST', data: { ids } });
};

export const getReferralByStudentId = async (studentId: string) => {
  return restClient.request<{ data: StudentReferral[]}>(`/api/v1/higher_ed/referral/by_about_user_id/${studentId}`,
    { method: 'GET' });
};

export const getReferralByAboutUserIds = async (ids: string[]) => {
  return restClient.request<{ data: Record<string, StudentReferral[]> }>('api/v1/higher_ed/referral/by_about_user_ids',
    { method: 'POST', data: { ids } });
};

export const getStudentContacts = async (studentId: string) => {
  return restClient.request<{ data: ContactsResponse}>(`/api/v1/view/higher_ed/student/${studentId}/contacts`,
    { method: 'GET' });
};
